import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/pages/About";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, ArrowRight, Layers, ListChecks, Sparkles, Mail, Star } from "lucide-react";
import { ReviewTimeline } from "@/components/ReviewTimeline";
import { contentApi } from "@/lib/api";

const CATS = [
  "Labor & Human Rights",
  "EHS & Environment",
  "Business Ethics & Anti-Corruption",
  "Responsible Procurement & Supplier Due Diligence",
  "Management System & Continuous Improvement",
  "AI Governance, Security & Responsible Use",
];

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

const LaunchBadge = () => (
  <span className="inline-flex items-center gap-1 rounded-full bg-wgreen-100 px-3 py-1 text-xs font-semibold text-wgreen-700" data-testid="launch-pricing-badge">
    <Sparkles className="h-3.5 w-3.5" /> 2026 Launch Pricing
  </span>
);

const FullCard = ({ years, price, standard, highlight }) => (
  <Card className={highlight ? "border-wgreen-600 shadow-[0_16px_40px_rgba(15,122,74,0.12)]" : ""} data-testid={`pricing-full-${years}`}>
    <CardContent className="flex h-full flex-col p-6 sm:p-8">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {highlight && <span className="w-fit rounded-full bg-navy-900 px-3 py-1 text-xs font-semibold text-white">Best value</span>}
        <LaunchBadge />
      </div>
      <p className="font-heading text-lg font-semibold text-navy-900">Full WECH-RB Certification</p>
      <p className="text-sm text-[var(--wechr-muted-text)]">All six assessment categories · {years} year{years > 1 ? "s" : ""} validity</p>
      <div className="mt-4 flex items-end gap-3">
        <p className="font-heading text-4xl font-bold text-navy-900" data-testid={`price-full-${years}`}>{money(price)}<span className="text-base font-medium text-[var(--wechr-muted-text)]"> USD</span></p>
      </div>
      <ul className="mt-5 space-y-2 text-sm text-navy-800">
        {CATS.map((c) => <li key={c} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-wgreen-600" />{c}</li>)}
        <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-wgreen-600" />Certificate valid for {years} year{years > 1 ? "s" : ""}</li>
      </ul>
      <Link to="/apply" className="mt-6"><Button className="btn-green w-full">Start application <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
    </CardContent>
  </Card>
);

const PerCategoryPrice = ({ years, price, standard }) => (
  <div className="rounded-xl border border-[var(--wechr-border)] p-5">
    <p className="text-sm font-medium text-navy-800">{years}-Year certification</p>
    <div className="mt-1 flex items-end gap-2">
      <p className="font-heading text-3xl font-bold text-navy-900" data-testid={`price-cat-${years}`}>{money(price)}</p>
      <span className="pb-1 text-base font-medium text-[var(--wechr-muted-text)]">/ category</span>
    </div>
  </div>
);

const Pricing = () => {
  const [pricing, setPricing] = useState({ full: { 1: 2250, 2: 5000 }, per_category: { 1: 450, 2: 1000 } });
  const [standard, setStandard] = useState({ full: { 1: 4500, 2: 10000 }, per_category: { 1: 900, 2: 2000 } });

  useEffect(() => {
    contentApi.framework().then(({ data }) => {
      if (data.pricing) setPricing(data.pricing);
      if (data.launch_pricing?.standard) setStandard(data.launch_pricing.standard);
    }).catch(() => {});
  }, []);

  return (
    <div>
      <PageHeader eyebrow="Pricing" title="WECH-RB Certification Pricing"
        subtitle="Choose full certification across all six categories, or certify only the categories you need. Select your scope and validity when you apply." />
      <section className="wechr-section">
        <div className="wechr-container space-y-10">

          {/* Launch banner */}
          <div className="flex flex-col items-start gap-3 rounded-2xl border border-wgreen-600/30 bg-wgreen-100 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="launch-pricing-banner">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white"><Sparkles className="h-5 w-5 text-wgreen-700" /></div>
              <div>
                <p className="font-heading text-sm font-semibold text-navy-900">2026 Launch Pricing</p>
                <p className="text-sm text-[var(--wechr-muted-text)]">The prices below are our current WECH-RB certification pricing for 2026.</p>
              </div>
            </div>
          </div>

          <div>
            <div className="mb-4 flex items-center gap-2"><Layers className="h-5 w-5 text-wgreen-700" /><h2 className="font-heading text-xl font-semibold text-navy-900">Full Certification</h2></div>
            <div className="grid gap-6 md:grid-cols-2">
              <FullCard years={1} price={pricing.full[1]} standard={standard.full[1]} />
              <FullCard years={2} price={pricing.full[2]} standard={standard.full[2]} highlight />
            </div>
          </div>

          <div>
            <div className="mb-4 flex items-center gap-2"><ListChecks className="h-5 w-5 text-wgreen-700" /><h2 className="font-heading text-xl font-semibold text-navy-900">Category-Based Certification</h2><LaunchBadge /></div>
            <Card data-testid="pricing-category">
              <CardContent className="p-6 sm:p-8">
                <p className="max-w-2xl text-sm text-[var(--wechr-muted-text)]">Prefer to certify just one or a few categories? Choose only the WECH-RB assessment categories relevant to your organization and pay per category.</p>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <PerCategoryPrice years={1} price={pricing.per_category[1]} standard={standard.per_category[1]} />
                  <PerCategoryPrice years={2} price={pricing.per_category[2]} standard={standard.per_category[2]} />
                </div>
                <p className="mt-4 text-xs text-[var(--wechr-muted-text)]">Example: certifying 3 categories for 2 years = 3 × {money(pricing.per_category[2])} = {money(pricing.per_category[2] * 3)} USD.</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {CATS.map((c) => <span key={c} className="rounded-full border border-[var(--wechr-border)] px-3 py-1 text-xs text-navy-800">{c}</span>)}
                </div>
                <Link to="/apply" className="mt-6 inline-block"><Button className="btn-green">Choose categories & apply <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
              </CardContent>
            </Card>
          </div>

          {/* Founding Organization CTA */}
          <Card className="border-navy-900/15 bg-navy-900 text-white" data-testid="founding-org-cta">
            <CardContent className="flex flex-col items-start gap-5 p-6 sm:p-8 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10"><Star className="h-6 w-6 text-gold-500" /></div>
                <div>
                  <h2 className="font-heading text-xl font-semibold">Request a Founding Organization Code via Email</h2>
                  <p className="mt-2 max-w-2xl text-sm text-white/80">
                    A limited number of early clients can join as <strong>WECH-RB Founding Organizations</strong>. Request a
                    founding code by email to discuss eligibility and benefits. With your approval, WECH-RB may feature your
                    company name and logo as a Founding Organization after successful certification.
                  </p>
                </div>
              </div>
              <Link to="/contact?topic=founding" className="shrink-0" data-testid="founding-org-request-button">
                <Button className="bg-white text-navy-900 hover:bg-white/90"><Mail className="mr-2 h-4 w-4" /> Request via email</Button>
              </Link>
            </CardContent>
          </Card>

          <ReviewTimeline />

          <p className="text-sm text-[var(--wechr-muted-text)]">All certifications are subject to WECH-RB's evidence-based document review and reviewer approval. See the <Link to="/assessment-criteria" className="text-wgreen-700 hover:underline">assessment criteria</Link> for how scoring works.</p>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
